import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { notificationsApi } from '../../../shared/lib/api'
import { Card, Tabs, Badge, Button, LoadingSpinner } from '../../../shared/components/UI'
import { notificationsQueryKey, useNotifications } from '../../../shared/hooks/useNotifications'

const ICON = {
  danger: <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0"><svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 3l6 10H2L8 3z" stroke="#DC2626" strokeWidth="1.3" /><path d="M8 7v3" stroke="#DC2626" strokeWidth="1.3" strokeLinecap="round" /></svg></div>,
  warning: <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0"><svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="#D97706" strokeWidth="1.3" /><path d="M8 5v4M8 11v.5" stroke="#D97706" strokeWidth="1.3" strokeLinecap="round" /></svg></div>,
  success: <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0"><svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 8l3.5 3.5L13 4" stroke="#16A34A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg></div>,
}

export default function NotificationsPage() {
  const [tab, setTab] = useState('All')
  const queryClient = useQueryClient()
  const { data, isLoading } = useNotifications({ limit: 100 })

  const items = data?.data || []

  const { mutate: ack } = useMutation({
    mutationFn: (id) => notificationsApi.acknowledge(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationsQueryKey }),
  })

  const { mutate: markAll, isPending: isMarkingAll } = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationsQueryKey }),
  })

  const visible = items.filter((notification) => {
    if (tab === 'Unread') return !notification.is_acknowledged
    if (tab === 'Variance Alerts') return matchesVarianceAlert(notification)
    if (tab === 'Wastage') return matchesWastage(notification)
    return true
  })

  if (isLoading) {
    return <div className="p-12 text-center"><LoadingSpinner /></div>
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div />
        <Button variant="secondary" onClick={() => markAll()} disabled={isMarkingAll}>
          {isMarkingAll ? 'Marking...' : 'Mark all read'}
        </Button>
      </div>

      <Tabs
        tabs={['All', 'Unread', 'Variance Alerts', 'Wastage']}
        active={tab}
        onChange={setTab}
      />

      <Card bodyClass="!p-0">
        {visible.length === 0 && <div className="text-center py-12 text-gray-400 text-sm">No notifications</div>}
        {visible.map((notification) => (
          <div
            key={notification.id}
            className={`flex gap-3 px-5 py-4 border-b border-gray-50 last:border-b-0 transition-colors ${!notification.is_acknowledged ? 'bg-brand-50/40' : 'bg-white'}`}
          >
            {ICON[getNotificationVariant(notification)] || ICON.warning}
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${!notification.is_acknowledged ? 'text-gray-900' : 'text-gray-600'}`}>{notification.title}</p>
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{notification.message}</p>
              <div className="flex items-center gap-3 mt-2">
                <span className="text-[11px] text-gray-400">{formatNotificationTime(notification.createdAt || notification.created_at)}</span>
                {!notification.is_acknowledged && (
                  <button onClick={() => ack(notification.id)} className="text-[11px] text-brand-600 font-medium hover:underline">
                    Acknowledge
                  </button>
                )}
                {notification.is_acknowledged && <Badge variant="gray">Acknowledged</Badge>}
              </div>
            </div>
          </div>
        ))}
      </Card>
    </div>
  )
}

function getNotificationVariant(notification) {
  const type = String(notification.type || '').toLowerCase()

  if (type.includes('variance') || type.includes('alert') || type.includes('danger')) return 'danger'
  if (type.includes('success') || type.includes('complete') || type.includes('completed')) return 'success'
  return 'warning'
}

function matchesVarianceAlert(notification) {
  const text = `${notification.type || ''} ${notification.title || ''} ${notification.message || ''}`.toLowerCase()
  return text.includes('variance') || text.includes('alert')
}

function matchesWastage(notification) {
  const text = `${notification.type || ''} ${notification.title || ''} ${notification.message || ''}`.toLowerCase()
  return text.includes('wastage')
}

function formatNotificationTime(value) {
  if (!value) return 'Recently'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Recently'

  const diffMs = Date.now() - date.getTime()
  const diffMinutes = Math.max(1, Math.floor(diffMs / 60000))

  if (diffMinutes < 60) return `${diffMinutes} min ago`

  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return `${diffHours} hr ago`

  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`

  return date.toLocaleString()
}
